import { useState, useEffect } from 'react';
import { Zap } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { getFeatureMeta, FEATURE_GROUP_ORDER } from '@/config/feature-catalog';

interface FeatureFlag {
  key: string;
  name: string;
  description: string | null;
  is_enabled: boolean;
  config: Record<string, unknown>;
}

const OTHER_GROUP = 'Other';

/** A flag enriched with its catalog metadata (falling back to DB values). */
interface DisplayFlag {
  key: string;
  label: string;
  description: string;
  group: string;
  is_enabled: boolean;
}

function toDisplayFlag(flag: FeatureFlag): DisplayFlag {
  const meta = getFeatureMeta(flag.key);
  return {
    key: flag.key,
    // Prefer the friendly catalog label; fall back to the stored name, then
    // the raw key (hydrated rows store name === key, description === "").
    label: meta?.label || flag.name || flag.key,
    description: meta?.hint || flag.description || '',
    group: meta?.group || OTHER_GROUP,
    is_enabled: flag.is_enabled,
  };
}

export default function FeatureToggles() {
  const queryClient = useQueryClient();
  const [flags, setFlags] = useState<DisplayFlag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Keys currently mid-save, so we can disable their toggle.
  const [savingKeys, setSavingKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchFlags();
  }, []);

  async function fetchFlags() {
    try {
      setIsLoading(true);
      setError(null);
      const response = await api.get<{ flags: FeatureFlag[] }>('/admin/feature-flags');
      setFlags((response.flags ?? []).map(toDisplayFlag));
    } catch (err) {
      setError('Failed to load feature toggles. Please try again.');
      console.error('Error fetching feature flags:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function toggleFlag(key: string, nextEnabled: boolean) {
    // Optimistic update so the switch feels instant.
    setError(null);
    setSuccessMessage(null);
    setFlags((prev) => prev.map((f) => (f.key === key ? { ...f, is_enabled: nextEnabled } : f)));
    setSavingKeys((prev) => new Set(prev).add(key));

    try {
      await api.put(`/admin/feature-flags/${key}`, { is_enabled: nextEnabled });
      // Refresh the public flag cache so the live nav, header, and storefront
      // pick up the change without a hard reload.
      queryClient.invalidateQueries({ queryKey: ['feature-flags'] });
      const label = flags.find((f) => f.key === key)?.label ?? key;
      setSuccessMessage(`${label} ${nextEnabled ? 'enabled' : 'disabled'}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      // Revert on failure.
      setFlags((prev) => prev.map((f) => (f.key === key ? { ...f, is_enabled: !nextEnabled } : f)));
      setError('Failed to update that toggle. Please try again.');
      console.error('Error updating feature flag:', err);
    } finally {
      setSavingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite">
        <LoadingSpinner />
        <span className="sr-only">Loading feature toggles...</span>
      </div>
    );
  }

  // Group flags by catalog group, preserving catalog order with "Other" last.
  const groupNames = [
    ...FEATURE_GROUP_ORDER.filter((g) => flags.some((f) => f.group === g)),
    ...(flags.some((f) => f.group === OTHER_GROUP) ? [OTHER_GROUP] : []),
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Zap className="w-8 h-8 text-gray-700" aria-hidden="true" />
          <h1 className="text-3xl font-bold text-gray-900">Feature Toggles</h1>
        </div>
        <p className="text-gray-600">
          Turn platform features on or off for this site. Changes take effect immediately.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" role="alert">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg" role="status" aria-live="polite">
          <p className="text-green-800">{successMessage}</p>
        </div>
      )}

      {flags.length === 0 ? (
        <Card padding="lg">
          <p className="text-stone-600">No feature toggles are configured for this site.</p>
        </Card>
      ) : (
        <div className="space-y-8">
          {groupNames.map((group) => (
            <section key={group}>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-stone-500 mb-3">{group}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {flags
                  .filter((f) => f.group === group)
                  .map((feature) => {
                    const saving = savingKeys.has(feature.key);
                    return (
                      <Card key={feature.key} padding="lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-4">
                            <h3 className="text-lg font-semibold text-stone-900 mb-1">{feature.label}</h3>
                            {feature.description && (
                              <p className="text-sm text-stone-600">{feature.description}</p>
                            )}
                            <p className="text-xs text-stone-400 mt-1 font-mono">{feature.key}</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer ml-4">
                            <input
                              type="checkbox"
                              className="sr-only peer"
                              checked={feature.is_enabled}
                              disabled={saving}
                              onChange={() => toggleFlag(feature.key, !feature.is_enabled)}
                            />
                            <span className="sr-only">
                              {feature.label} {feature.is_enabled ? 'enabled' : 'disabled'}
                            </span>
                            <div className="w-11 h-6 bg-stone-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-stone-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600 peer-disabled:opacity-50"></div>
                          </label>
                        </div>
                      </Card>
                    );
                  })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
